import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";
import { KeyRound, Loader2, ShieldCheck, Anchor, CheckCircle2, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1561728130-afd430af0493?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FRIGATES = ["Cherokee", "Cumberland", "Pelican"];

export default function Signup() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [checking, setChecking] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", deck_name: "", frigate: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const useCode = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) return;
    setChecking(true); setCodeErr("");
    try { await api.get(`/join/${c}`); navigate(`/join/${c}`); } catch (e2) { setCodeErr(errMsg(e2)); } finally { setChecking(false); }
  };
  const request = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const { data } = await api.post("/access-requests", { ...f, frigate: f.frigate || null }); setSent(data.message); }
    catch (e2) { setError(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-5/12 overflow-hidden lg:block">
        <img src={HERO} alt="Galleon at sea" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030A16] via-[#030A16]/70 to-[#030A16]/30" />
        <div className="relative z-10 flex h-full flex-col justify-end p-12">
          <div>
            <img src="/ban-logo.jpg" alt="Brothers Across Nigeria Int'l" className="mb-6 h-32 w-32 rounded-full object-cover shadow-2xl ring-4 ring-[hsl(var(--gold))]/50 animate-float" />
            <p className="label-eyebrow">Brothers of the Galleon only</p>
            <h1 className="mt-3 font-display text-5xl font-800 leading-tight text-white">Request your <span className="gold-text">Berth</span></h1>
            <p className="mt-4 max-w-md text-sm text-slate-300">This vessel is reserved for salted brothers of Brothers Across Nigeria Int'l — IOTA Galleon. Already a brother but not yet aboard? Board with the access code your officer sent you, or request your berth and an officer will verify you.</p>
          </div>
        </div>
      </div>
      <div className="sea-bg flex w-full flex-col items-center justify-center overflow-y-auto p-6 lg:w-7/12">
        <div className="w-full max-w-xl space-y-5">
          <div className="mb-2 lg:hidden"><Logo /></div>
          <form onSubmit={useCode} className="glass rounded-2xl p-6 shadow-2xl" data-testid="signup-code-form">
            <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[hsl(var(--gold))]" /><h2 className="font-display text-xl font-700">I have an access code</h2></div>
            <p className="mt-1 text-xs text-muted-foreground">Codes look like <span className="font-mono-data">BAN-XXXX-XXXX</span> — your officer shares them by WhatsApp or link.</p>
            <div className="mt-3 flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BAN-XXXX-XXXX" className="font-mono-data uppercase tracking-widest" data-testid="signup-code-input" />
              <Button type="submit" disabled={checking || !code.trim()} className="bg-[hsl(var(--gold))] font-700 text-[#0A192F] hover:bg-[hsl(var(--gold))]/90" data-testid="signup-code-button">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}</Button>
            </div>
            {codeErr && <p className="mt-2 text-xs text-destructive" data-testid="signup-code-error">{codeErr}</p>}
          </form>

          {sent ? (
            <div className="glass rounded-2xl p-8 text-center shadow-2xl" data-testid="signup-request-sent">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <h2 className="mt-3 font-display text-2xl font-700">Request sent</h2>
              <p className="mt-2 text-sm text-muted-foreground">{sent}</p>
              <Link to="/login" className="mt-6 inline-block text-sm text-[hsl(var(--water))] hover:underline" data-testid="signup-back-login">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={request} className="glass rounded-2xl p-6 shadow-2xl" data-testid="signup-request-form">
              <div className="flex items-center gap-2"><Anchor className="h-5 w-5 text-[hsl(var(--water))]" /><h2 className="font-display text-xl font-700">Galleon brother without a code? Request access</h2></div>
              <p className="mt-1 text-xs text-muted-foreground">For existing brothers of the IOTA Galleon only. An officer verifies you with the brother you name below, then sends your personal access link.</p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label>Full name <span className="text-[hsl(var(--gold))]">*</span></Label><Input required value={f.name} onChange={set("name")} placeholder="e.g. Emeka Obi" className="mt-1" data-testid="signup-name-input" /></div>
                <div><Label>Deck name</Label><Input value={f.deck_name} onChange={set("deck_name")} placeholder="e.g. Stormrider" className="mt-1" data-testid="signup-deck-input" /></div>
                <div><Label>Email <span className="text-[hsl(var(--gold))]">*</span></Label><Input required type="email" value={f.email} onChange={set("email")} placeholder="you@example.com" className="mt-1" data-testid="signup-email-input" /></div>
                <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} placeholder="+234 801 234 5678" className="mt-1" data-testid="signup-phone-input" /></div>
                <div>
                  <Label>Frigate</Label>
                  <Select value={f.frigate} onValueChange={(v) => setF((s) => ({ ...s, frigate: v }))}>
                    <SelectTrigger className="mt-1" data-testid="signup-frigate-select"><SelectValue placeholder="Which frigate?" /></SelectTrigger>
                    <SelectContent>{FRIGATES.map((fr) => <SelectItem key={fr} value={fr}>{fr}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Brother / officer who can vouch for you <span className="text-[hsl(var(--gold))]">*</span></Label><Input required value={f.note} onChange={set("note")} placeholder="e.g. FC Tunde Bello" className="mt-1" data-testid="signup-note-input" /></div>
              </div>
              {error && <p className="mt-3 text-sm text-destructive" data-testid="signup-error">{error}</p>}
              <Button type="submit" disabled={busy} className="mt-5 w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F] hover:bg-[hsl(var(--gold))]/90" data-testid="signup-request-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request access"}</Button>
              <p className="mt-4 text-center text-xs text-muted-foreground">Already aboard? <Link to="/login" className="text-[hsl(var(--water))] hover:underline" data-testid="signup-login-link">Sign in</Link></p>
            </form>
          )}
          <p className="flex items-center justify-center gap-2 text-center text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Confidential system. Not open to the public — brothers of the Galleon only, by officer verification.</p>
        </div>
      </div>
    </div>
  );
}
